import React from 'react';
import PropTypes from 'prop-types';

import { MAX_LIST_NAME_LENGTH } from '../listErrors';
import countCharacters from '../../../utils/countCharacters';

import styles from './listPicker.scss';

const NEW_LIST_LABEL = 'Create New List';
const EXISTING_LIST_LABEL = 'Choose from Existing List';
const FILTER_PLACEHOLDER = 'Filter lists';
const NO_LISTS_MESSAGE = 'No lists yet.';
const NO_FILTER_MATCHES_MESSAGE = 'No lists match that filter.';
const LOADING_MESSAGE = 'Loading...';

const ListRow = ({ 'data-testid': dataTestId, list, checked, onToggle }) => (
    <label
        className={styles.listRow}
        htmlFor={dataTestId && `${dataTestId}-checkbox-${list.id}`}
        data-testid={dataTestId && `${dataTestId}.list-row-${list.id}`}
    >
        <input
            id={dataTestId && `${dataTestId}-checkbox-${list.id}`}
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            data-testid={dataTestId && `${dataTestId}.list-checkbox-${list.id}`}
        />
        <span data-testid={dataTestId && `${dataTestId}.list-row-${list.id}.name`}>{list.name}</span>
    </label>
);

ListRow.propTypes = {
    'data-testid': PropTypes.string,
    list: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    checked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

ListRow.defaultProps = {
    'data-testid': null,
};

// Shared "Create New List + Choose from Existing List" picker body for the SavedLists modals
// (SaveToListModal single-player, AddPlayerToListModal multi-player). Fully controlled — all state
// lives in useListPicker on the owning modal, which also owns the save. Every testid derives from
// the caller's `data-testid` prefix so each modal keeps its own exact contract
// (`save-to-list-modal.*` / `add-player-modal.*`).
//
// Native input/checkboxes, not the shared Form/Input + CheckboxSA components: the ticket data-testid
// contracts require the exact literal ids emitted here (the web verifier's Playwright selectors
// depend on them). Form/Input's controlled mode always suffixes its input/error with `.field`/
// `.error`, and CheckboxSA hides the native input behind a separate unlabeled span with no testid
// slot — either would break an exact-id, real-click-driven contract.
const ListPicker = ({
    'data-testid': dataTestId,
    isLoadingLists,
    defaultList,
    filteredTailLists,
    isFilteredToZero,
    checkedListIds,
    onToggleList,
    newListName,
    onNewListNameChange,
    nameError,
    showFilter,
    filterText,
    onFilterTextChange,
}) => {
    const newListInputId = dataTestId && `${dataTestId}-new-list-input`;
    return (
        <>
            <label
                className={styles.fieldLabel}
                htmlFor={newListInputId}
                data-testid={dataTestId && `${dataTestId}.new-list-label`}
            >
                {NEW_LIST_LABEL}
            </label>
            <input
                id={newListInputId}
                type="text"
                className={styles.newListInput}
                value={newListName}
                onChange={onNewListNameChange}
                data-testid={dataTestId && `${dataTestId}.new-list-input`}
            />
            <div className={styles.charCounter} data-testid={dataTestId && `${dataTestId}.char-counter`}>
                {`${countCharacters(newListName)}/${MAX_LIST_NAME_LENGTH}`}
            </div>
            {nameError && (
                <div className={styles.nameError} data-testid={dataTestId && `${dataTestId}.name-error`}>
                    {nameError}
                </div>
            )}
            <div className={styles.divider} />
            <div className={styles.existingHeader} data-testid={dataTestId && `${dataTestId}.existing-header`}>
                {EXISTING_LIST_LABEL}
            </div>
            {showFilter && (
                <input
                    type="text"
                    className={styles.filterInput}
                    placeholder={FILTER_PLACEHOLDER}
                    value={filterText}
                    onChange={onFilterTextChange}
                    data-testid={dataTestId && `${dataTestId}.filter-input`}
                />
            )}
            <div className={styles.listScroll} data-testid={dataTestId && `${dataTestId}.list-scroll`}>
                {isLoadingLists ? (
                    <div className={styles.loading} data-testid={dataTestId && `${dataTestId}.loading`}>
                        {LOADING_MESSAGE}
                    </div>
                ) : (
                    <>
                        {defaultList && (
                            <ListRow
                                data-testid={dataTestId}
                                list={defaultList}
                                checked={checkedListIds.has(String(defaultList.id))}
                                onToggle={() => onToggleList(defaultList.id)}
                            />
                        )}
                        {filteredTailLists.map((list) => (
                            <ListRow
                                key={list.id}
                                data-testid={dataTestId}
                                list={list}
                                checked={checkedListIds.has(String(list.id))}
                                onToggle={() => onToggleList(list.id)}
                            />
                        ))}
                        {filteredTailLists.length === 0 && (isFilteredToZero || !defaultList) && (
                            <div className={styles.emptyState} data-testid={dataTestId && `${dataTestId}.empty-state`}>
                                {isFilteredToZero ? NO_FILTER_MATCHES_MESSAGE : NO_LISTS_MESSAGE}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

ListPicker.propTypes = {
    'data-testid': PropTypes.string,
    isLoadingLists: PropTypes.bool.isRequired,
    defaultList: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
    }),
    filteredTailLists: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
    })).isRequired,
    isFilteredToZero: PropTypes.bool.isRequired,
    checkedListIds: PropTypes.instanceOf(Set).isRequired,
    onToggleList: PropTypes.func.isRequired,
    newListName: PropTypes.string.isRequired,
    onNewListNameChange: PropTypes.func.isRequired,
    nameError: PropTypes.string,
    showFilter: PropTypes.bool.isRequired,
    filterText: PropTypes.string.isRequired,
    onFilterTextChange: PropTypes.func.isRequired,
};

ListPicker.defaultProps = {
    'data-testid': null,
    defaultList: null,
    nameError: null,
};

export default ListPicker;
